import {Injectable, isDevMode} from "@angular/core";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {Hospitals} from "../interfaces/Hospitals";
import {environment} from "../../environments/environment";
import {combineLatest, Observable} from "rxjs";
import {map, take} from "rxjs/operators";

@Injectable({
    providedIn: 'root'
})

export class HospitalService {
    env = isDevMode() ? environment.testMode : environment.productionMode

    constructor(private mFirestore: AngularFirestore) {
    }

    private get collection() {
        return this.mFirestore.collection<Hospitals>(this.env.hospitals);
    }

    /** Firestore rejects `undefined` field values, so drop them before writing. */
    private clean<T>(value: T): T {
        return JSON.parse(JSON.stringify(value ?? {}));
    }

    addHospital(hospital: any) {
        return this.collection.add(this.clean(hospital));
    }

    updatehospital(key: string, hospital: Partial<Hospitals>) {
        return this.collection.doc(key).set(this.clean(hospital) as any, {merge: true});
    }

    getHospitalById(key: string): Observable<Hospitals | null> {
        return this.collection.doc(key).snapshotChanges().pipe(
            map(snapshot => snapshot.payload.exists
                ? {...snapshot.payload.data(), id: snapshot.payload.id} as Hospitals
                : null)
        );
    }

    /** Every hospital with its document id in `id`. */
    getAllHospitals(): Observable<Hospitals[]> {
        return this.mFirestore.collection<Hospitals>(this.env.hospitals).valueChanges({idField: 'id'});
    }

    getHospitalList(): Observable<Hospitals[]> {
        return this.getAllHospitals().pipe(
            map(hospitals => hospitals.sort((a, b) => (a.hospitalName || '').localeCompare(b.hospitalName || '')))
        );
    }

    /**
     * Hospitals linked to any of the given centres (a hospital can serve several centres).
     * An empty centre list returns every hospital.
     */
    getHospitalListByCentre(centreCodes: string[]): Observable<Hospitals[]> {
        const codes = Array.from(new Set(centreCodes || []));
        if (!codes.length) return this.getHospitalList();

        // array-contains-any accepts at most 10 values, so query in chunks and merge by id
        const chunks: string[][] = [];
        for (let i = 0; i < codes.length; i += 10) chunks.push(codes.slice(i, i + 10));

        return combineLatest(chunks.map(chunk =>
            this.mFirestore.collection<Hospitals>(this.env.hospitals,
                ref => ref.where('forCentres', 'array-contains-any', chunk)
            ).valueChanges({idField: 'id'})
        )).pipe(
            map(results => {
                const byId = new Map<string, Hospitals>();
                results.forEach(list => list.forEach(h => byId.set(h.id, h)));
                return Array.from(byId.values())
                    .sort((a, b) => (a.hospitalName || '').localeCompare(b.hospitalName || ''));
            })
        );
    }

    deleteHospital(id: string) {
        return this.collection.doc(id).delete();
    }

    getHospitalCount(): Observable<number> {
        return this.getAllHospitals().pipe(map(hospitals => hospitals.length));
    }

    getHospitalMSWCount(): Observable<number> {
        return this.getAllHospitals().pipe(
            map(hospitals => hospitals.filter(h => h.socialWorkerName && h.socialWorkerName !== "None").length)
        );
    }

    /** True when a hospital with the same name already serves one of the given centres. */
    checkDuplicateHospital(hospitalName: string, centreCodes: string[], excludeKey?: string): Observable<boolean> {
        const name = hospitalName?.toLowerCase().trim();
        return this.getHospitalListByCentre(centreCodes).pipe(
            take(1),
            map(hospitals => hospitals.some(h =>
                h.hospitalName?.toLowerCase().trim() === name && (!excludeKey || h.id !== excludeKey)
            ))
        );
    }
}
